import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Phone, MessageCircle, CheckCircle2, ArrowRight, Clock3,
  BadgeCheck, MapPin, ChevronRight,
} from "lucide-react";
import { SplitLines, FadeUp, Eyebrow } from "../components/Reveal";
import Faq from "../components/Faq";
import ContactForm from "../components/ContactForm";
import { CtaBanner } from "../components/CtaBanner";
import { SERVICES, getService } from "../data/services";
import { PHONE_TEL, PHONE_DISPLAY, waLink } from "../data/site";

export default function ServicePage() {
  const { slug } = useParams();
  const service = getService(slug);

  useEffect(() => {
    if (service) document.title = `${service.title} | Motion Tyres 24/7`;
    return () => { document.title = "Motion Tyres | 24/7 Mobile Tyre Fitting — We Come To You"; };
  }, [service]);

  if (!service) {
    return (
      <div className="pt-40 pb-24 max-w-7xl mx-auto px-5 sm:px-8" data-testid="service-not-found">
        <h1 className="font-display font-extrabold uppercase text-5xl">Service not found</h1>
        <Link to="/" className="text-[#FF5500] underline mt-4 inline-block">Back to home</Link>
      </div>
    );
  }

  const others = SERVICES.filter((s) => s.slug !== slug);
  const waMsg = `Hi Motion Tyres — I'm interested in your ${service.shortName} service.`;

  return (
    <div data-testid={`service-page-${slug}`}>
      {/* HERO */}
      <section className="relative pt-28 sm:pt-36 pb-14 sm:pb-20 overflow-hidden" data-testid="service-hero">
        <div
          className="absolute top-0 right-0 w-[560px] h-[560px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(255,85,0,0.14) 0%, transparent 70%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.nav
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
              className="flex items-center gap-2 font-telemetry text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-6"
              aria-label="Breadcrumb"
            >
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-[#FF5500]">{service.shortName}</span>
            </motion.nav>

            <Eyebrow>Service — 24/7 mobile</Eyebrow>
            <h1 className="font-display font-extrabold uppercase leading-[0.92] text-5xl sm:text-7xl mt-4">
              <SplitLines lines={service.heroLines} delay={0.25} accentLast />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.6 }}
              className="mt-5 text-zinc-400 text-lg max-w-lg"
            >
              {service.tagline}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.6 }}
              className="mt-8 flex flex-col sm:flex-row gap-3"
            >
              <a
                href={PHONE_TEL}
                data-testid="service-hero-call-button"
                className="btn-glow flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#FF5500] hover:bg-[#FF6A1A] text-black font-telemetry text-sm font-semibold uppercase tracking-[0.12em]"
              >
                <Phone className="w-4 h-4" /> Call {PHONE_DISPLAY}
              </a>
              <a
                href={waLink(waMsg)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="service-hero-whatsapp-button"
                className="flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-emerald-500/70 text-emerald-400 hover:bg-emerald-500 hover:text-black transition-colors font-telemetry text-sm uppercase tracking-[0.12em]"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="frame-cut overflow-hidden border border-[#27272A]"
          >
            <img
              src={service.image}
              alt={service.title}
              className="w-full h-[340px] sm:h-[460px] object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* FACT BAR */}
      <section className="border-y border-[#27272A] bg-[#131316]" data-testid="service-fact-bar">
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#27272A]">
          {[
            { i: Clock3, t: "Response", v: "30–60 min, 24/7" },
            { i: MapPin, t: "Where", v: "Your home, work or roadside" },
            { i: BadgeCheck, t: "Brands", v: "All major tyre brands" },
            { i: Phone, t: "Dispatch line", v: PHONE_DISPLAY },
          ].map(({ i: Icon, t, v }) => (
            <div key={t} className="bg-[#131316] px-6 py-6">
              <Icon className="w-4 h-4 text-[#FF5500] mb-2" />
              <p className="font-telemetry text-[10px] uppercase tracking-[0.25em] text-zinc-500">{t}</p>
              <p className="font-display font-bold uppercase text-lg text-white mt-1">{v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* INTRO */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="service-intro">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <FadeUp>
              <Eyebrow>In detail</Eyebrow>
              <h2 className="font-display font-extrabold uppercase text-3xl sm:text-5xl mt-3 mb-8 leading-[0.95]">
                Done properly,
                <br />wherever you're parked.
              </h2>
            </FadeUp>
            {service.intro.map((p, i) => (
              <FadeUp key={i} delay={i * 0.08}>
                <p className="text-zinc-400 text-base sm:text-lg leading-relaxed mb-6">{p}</p>
              </FadeUp>
            ))}
          </div>
          <div className="lg:col-span-5">
            <FadeUp delay={0.1}>
              <div className="frame-cut overflow-hidden border border-[#27272A]">
                <img
                  src={service.imageAlt}
                  alt={`${service.title} detail`}
                  className="w-full h-80 object-cover"
                  loading="lazy"
                />
              </div>
              <div className="mt-6 bg-[#131316] border border-[#27272A] p-6">
                <p className="font-telemetry text-[10px] uppercase tracking-[0.25em] text-[#FF5500] mb-3">
                  Fixed quote, no surprises
                </p>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  Every job is quoted up front on WhatsApp or by phone — tyre,
                  fitting, balancing and disposal included in one number.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* INCLUDED + SIGNS */}
      <section className="border-y border-[#27272A] bg-[#0E0E10]" data-testid="service-included-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div>
            <FadeUp>
              <Eyebrow>Every visit includes</Eyebrow>
              <h3 className="font-display font-extrabold uppercase text-3xl sm:text-4xl mt-3 mb-8">
                Nothing left to chance.
              </h3>
            </FadeUp>
            <div className="space-y-4">
              {service.included.map((item, i) => (
                <FadeUp key={item} delay={i * 0.05} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#FF5500] shrink-0 mt-0.5" />
                  <span className="text-zinc-300">{item}</span>
                </FadeUp>
              ))}
            </div>
          </div>
          <div>
            <FadeUp>
              <Eyebrow>Good to know</Eyebrow>
              <h3 className="font-display font-extrabold uppercase text-3xl sm:text-4xl mt-3 mb-8">
                {service.signsTitle}
              </h3>
            </FadeUp>
            <div className="space-y-3">
              {service.signs.map((item, i) => (
                <FadeUp key={item} delay={i * 0.05} className="flex items-start gap-3 bg-[#131316] border border-[#27272A] px-5 py-4">
                  <span className="font-telemetry text-xs text-[#FF5500] mt-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-sm text-zinc-300 leading-relaxed">{item}</span>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="service-process-section">
        <FadeUp>
          <Eyebrow>How it works</Eyebrow>
          <h3 className="font-display font-extrabold uppercase text-3xl sm:text-5xl mt-3 mb-12">
            Four steps to done.
          </h3>
        </FadeUp>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {service.process.map((step, i) => (
            <FadeUp key={step.n} delay={i * 0.08} className="bg-[#131316] border border-[#27272A] p-6 card-line">
              <p className="font-display font-extrabold text-5xl text-outline">{step.n}</p>
              <h4 className="font-display font-bold uppercase text-xl mt-4 text-white">{step.t}</h4>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{step.d}</p>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-[#27272A] bg-[#0E0E10]" data-testid="service-faq-section">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <FadeUp>
            <Eyebrow>Questions</Eyebrow>
            <h3 className="font-display font-extrabold uppercase text-3xl sm:text-5xl mt-3 mb-10">
              Asked before every job.
            </h3>
          </FadeUp>
          <Faq items={service.faqs} prefix={`svc-${slug}`} />
        </div>
      </section>

      {/* OTHER SERVICES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16" data-testid="service-others-section">
        <FadeUp>
          <p className="font-telemetry text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-6">
            Other services from Motion Tyres
          </p>
          <div className="flex flex-wrap gap-3">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={`/services/${o.slug}`}
                data-testid={`service-link-${o.slug}`}
                className="group flex items-center gap-2 px-5 py-3 rounded-full border border-[#27272A] hover:border-[#FF5500]/60 transition-colors text-sm text-zinc-300 hover:text-white"
              >
                {o.shortName}
                <ArrowRight className="w-3.5 h-3.5 text-[#FF5500] transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            ))}
          </div>
        </FadeUp>
      </section>

      <CtaBanner />

      {/* BOOKING */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="service-booking-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <FadeUp>
            <Eyebrow>Book this service</Eyebrow>
            <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl mt-3 leading-[0.95]">
              Get {service.shortName.toLowerCase()}
              <br />
              <span className="text-[#FF5500]">booked today.</span>
            </h2>
            <p className="mt-6 text-zinc-400 max-w-md leading-relaxed">
              The form sends your enquiry — with your live location if you
              allow it — straight to our dispatch WhatsApp. A human replies,
              usually within minutes.
            </p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <ContactForm defaultService={service.shortName} />
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
